// SPDX-License-Identifier: AGPL-3.0-or-later

import {PreloadableUserPopout} from '@app/features/channel/components/PreloadableUserPopout';
import styles from '@app/features/ui/avatars/AvatarStack.module.css';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {User} from '@app/features/user/models/User';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React from 'react';
export interface AvatarStackProps {
	children?: React.ReactNode;
	users?: ReadonlyArray<User>;
	size?: number;
	maxVisible?: number;
	overlap?: number;
	className?: string;
	guildId?: string | null;
	channelId?: string | null;
	renderAvatar?: (user: User, size: number) => React.ReactNode;
	enableProfileModal?: boolean;
	showTooltips?: boolean;
	remainingContent?: React.ReactNode;
	onUserContextMenu?: (event: React.MouseEvent<HTMLElement>, user: User) => void;
}
export const AvatarStack: React.FC<AvatarStackProps> = observer(
	({
		children,
		users,
		size = 28,
		maxVisible = 3,
		overlap,
		className,
		guildId,
		channelId,
		renderAvatar,
		enableProfileModal = true,
		showTooltips = true,
		remainingContent,
		onUserContextMenu,
	}) => {
		const {t} = useLingui();
		const childArray = React.Children.toArray(children).filter(Boolean);
		const userChildren =
			users?.map((user) => {
				const displayName = NicknameUtils.getNickname(user, guildId ?? undefined, channelId ?? undefined);
				const avatarNode = renderAvatar?.(user, size) ?? (
					<Avatar user={user} size={size} guildId={guildId ?? undefined} />
				);
				if (!avatarNode) return null;
				if (enableProfileModal) {
					const profileTrigger = (
						<FocusRing offset={-2}>
							<button type="button" className={styles.avatarButton} aria-label={t`Open profile for ${displayName}`}>
								{avatarNode}
							</button>
						</FocusRing>
					);
					return (
						<PreloadableUserPopout
							key={user.id}
							user={user}
							isWebhook={false}
							guildId={guildId ?? undefined}
							channelId={channelId ?? undefined}
							disableContextMenu={true}
							tooltip={showTooltips ? displayName : undefined}
						>
							{profileTrigger}
						</PreloadableUserPopout>
					);
				}
				const content = <div className={styles.avatarContent}>{avatarNode}</div>;
				if (!showTooltips) {
					return React.cloneElement(content, {key: user.id});
				}
				return (
					<Tooltip key={user.id} text={displayName}>
						{content}
					</Tooltip>
				);
			}) ?? [];
		const resolvedChildren = users ? userChildren.filter(Boolean) : childArray;
		const totalCount = resolvedChildren.length;
		const visibleChildren = resolvedChildren.slice(0, maxVisible);
		const remainingCount = Math.max(0, totalCount - maxVisible);
		const computedOutline = Math.min(3, Math.max(1, Math.round(size * 0.05)));
		const computedOverlap = overlap !== undefined ? overlap : Math.round(-0.35 * size);
		const cssVars = {
			'--avatar-size': `${size}px`,
			'--avatar-overlap': `${computedOverlap}px`,
			'--avatar-outline': `${computedOutline}px`,
		} as React.CSSProperties;
		const wrapWithContextMenu = (node: React.ReactNode, user: User) => {
			if (!onUserContextMenu) return node;
			const displayName = NicknameUtils.getNickname(user, guildId ?? undefined, channelId ?? undefined);
			return (
				<div
					className={styles.avatarContextMenuWrap}
					onContextMenu={(e) => onUserContextMenu(e, user)}
					role="group"
					aria-label={displayName ?? user.username}
				>
					{node}
				</div>
			);
		};
		const userChildrenWithContextMenu =
			users && onUserContextMenu
				? userChildren.map((child, index) => {
						const user = users[index];
						return user ? wrapWithContextMenu(child, user) : child;
					})
				: resolvedChildren;
		const finalVisibleChildren = users ? userChildrenWithContextMenu.slice(0, maxVisible) : visibleChildren;
		return (
			<div className={clsx(styles.container, className)} style={cssVars}>
				{finalVisibleChildren.map((child, index) => (
					<div
						key={index}
						className={clsx(
							styles.avatar,
							(index < finalVisibleChildren.length - 1 || remainingCount > 0) && styles.withMask,
						)}
					>
						{child}
					</div>
				))}
				{remainingCount > 0 && (remainingContent ?? <div className={styles.remainingCount}>+{remainingCount}</div>)}
			</div>
		);
	},
);
